import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Crown, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';

/**
 * 교육 메인 상단 "우수 인증 교육 기관" 쇼케이스.
 * 2026-10-10: 목업('메뉴바 및 교육 메인')에 맞춰 좌측 소개 패널 + 우측 4열 카드 그리드로 교체.
 * 기본은 4개씩 페이지 단위로 보여주고, '펼쳐보기'로 전체를 한 번에 펼친다.
 */

export interface FeaturedProvider {
  id: string;
  company_name: string | null;
  banner_image_url: string | null;
  address: string | null;
  tagline: string | null;
}

const PAGE_SIZE = 4;
const INTRO_COPY = '검증된 교육기관과 함께,\n더 전문적인 피트니스의 내일을 만들어갑니다.';
const FALLBACK_BANNER =
  'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop';

interface FeaturedProvidersProps {
  providers: FeaturedProvider[];
  /** 관리자가 편집하는 선정 기준 안내 — 제목 옆 물음표 툴팁으로 노출 */
  intro?: string;
}

export default function FeaturedProviders({ providers, intro = '' }: FeaturedProvidersProps) {
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const totalPages = Math.max(1, Math.ceil(providers.length / PAGE_SIZE));

  // 기관 수가 줄어 현재 페이지가 사라지는 경우 보정
  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
  }, [page, totalPages]);

  const visibleProviders = useMemo(
    () => (expanded ? providers : providers.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)),
    [providers, expanded, page]
  );

  const introTooltip = intro.trim();
  const hasMultiplePages = totalPages > 1;

  return (
    <section
      className="relative grid grid-cols-1 lg:grid-cols-[minmax(320px,0.9fr)_minmax(0,2.1fr)] items-center gap-6 rounded-3xl border border-orange-100 px-5 pt-10 pb-5 sm:px-8 sm:pt-14 sm:pb-8 shadow-sm"
      style={{ background: 'linear-gradient(110deg, #fffaf3 0%, #fff7ed 48%, #fffaf5 100%)' }}
    >
      {/* 좌측 소개 패널 */}
      <div className="flex flex-col items-start justify-center gap-2.5">
        <p className="text-[0.72rem] font-extrabold tracking-[0.13em] text-accent sm:whitespace-nowrap">
          GOOD EDUCATION MAKES A HEALTHY TOMORROW
        </p>
        <div className="flex items-center gap-2">
          <Crown className="w-7 h-7 text-accent shrink-0" />
          <h2 className="text-[clamp(1.7rem,2.3vw,2.4rem)] font-bold text-gray-900 leading-[1.15]">
            우수 인증 교육 기관
          </h2>
          {introTooltip && (
            <div className="group relative flex items-center">
              <button
                type="button"
                aria-label="우수 인증 교육 기관 선정 기준"
                className="text-gray-400 hover:text-accent transition-colors focus:outline-none"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
              {/* 호버 툴팁 */}
              <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-72 -translate-x-1/2 rounded-xl bg-gray-900 p-4 text-sm leading-relaxed text-white opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100 sm:w-80">
                {/* 말풍선 꼬리 */}
                <span className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 bg-gray-900" />
                <span className="relative whitespace-pre-line">{introTooltip}</span>
              </div>
            </div>
          )}
        </div>
        <p className="max-w-[18rem] text-[0.94rem] leading-[1.7] text-gray-500 whitespace-pre-line">
          {INTRO_COPY}
        </p>

        {/* 페이지 슬라이더 — 펼친 상태에서는 전체가 보이므로 숨김 */}
        {!expanded && hasMultiplePages && (
          <div className="mt-1 inline-flex items-center gap-2.5" aria-label="우수 인증 교육 기관 슬라이드 위치">
            <button
              type="button"
              onClick={() => setPage((p) => (p - 1 + totalPages) % totalPages)}
              aria-label="이전 기관"
              className="inline-flex items-center justify-center w-6 h-6 text-gray-500 hover:text-accent transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="inline-flex items-center gap-2.5">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setPage(index)}
                  aria-label={`기관 목록 ${index + 1}페이지`}
                  aria-current={page === index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    page === index ? 'bg-accent' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => (p + 1) % totalPages)}
              aria-label="다음 기관"
              className="inline-flex items-center justify-center w-6 h-6 text-gray-500 hover:text-accent transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* 우측 카드 그리드 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {visibleProviders.map((provider) => (
          <Link
            key={provider.id}
            to={`/providers/${provider.id}`}
            className="overflow-hidden bg-white rounded-xl border border-gray-200/70 shadow-soft hover:shadow-md transition-shadow group"
          >
            <div className="relative">
              <img
                src={provider.banner_image_url || FALLBACK_BANNER}
                alt={provider.company_name || '우수 인증 교육 기관'}
                className="w-full block object-cover bg-gray-100"
                style={{ aspectRatio: '16/9' }}
              />
              <div className="absolute top-1.5 right-1.5">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-accent text-white rounded-full text-[10px] font-semibold">
                  <Crown className="w-2.5 h-2.5" />
                  인증 우수
                </span>
              </div>
            </div>
            <div className="p-2.5">
              <h3 className="font-bold text-sm text-gray-900 mb-0.5 line-clamp-1">
                {provider.company_name || '우수 인증 교육 기관'}
              </h3>
              {provider.address && <p className="text-xs text-gray-500 line-clamp-1">{provider.address}</p>}
              {provider.tagline && (
                <p className="text-xs text-accent font-medium mt-1 line-clamp-2">{provider.tagline}</p>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* 펼쳐보기 / 접어보기 — 한 페이지에 다 들어가면 숨김 */}
      {providers.length > PAGE_SIZE && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="lg:col-start-2 justify-self-end -mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-orange-200 bg-white text-[0.82rem] font-bold text-gray-700 hover:text-accent hover:border-accent transition-colors"
        >
          {expanded ? (
            <>
              접어보기 <ChevronUp className="w-4 h-4" />
            </>
          ) : (
            <>
              펼쳐보기 <ChevronDown className="w-4 h-4" />
            </>
          )}
        </button>
      )}
    </section>
  );
}
